import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useQuoteModal } from "../lib/quoteModal";
import { useCompareStash } from "../lib/compareStash";
import { track, events } from "../lib/analytics";
import {
  CTA_INSURANCE,
  CTA_AUTO_LOAN,
  CTA_REFINANCE,
  CTA_WARRANTY,
  CTA_SAVINGS_CALCULATOR,
} from "../lib/ctaVocabulary";

// Pages where the bottom bar should NOT appear.
// /, /contact, /privacy, /terms — already have a dedicated hero CTA so the
// sticky bar would be redundant. /admin/* is a back-office surface (no
// sticky retail CTA needed). /editorial-standards, /methodology, /how-we-
// make-money are static legal pages.
//
// Iter-192.F — Added the 4 vertical hub paths (/auto-insurance,
// /auto-loans, /refinance, /warranty) because MobileStickyCompareCTA
// owns the bottom-bar slot on those paths. Previously BOTH bars
// rendered simultaneously on hubs (z-40 stacked under z-[100]),
// producing the visible two-bar bug reported in the iter-192.F audit.
// Iter-274.M — Extended to include the 4 iter-274 apex hubs for the
// same reason (MobileStickyCompareCTA now owns those slots too —
// preventing a regression of the iter-192.F stacking bug).
const HIDDEN_ROUTES_EXACT = [
  "/",
  "/contact",
  "/privacy",
  "/terms",
  "/accessibility",
  "/auto-insurance",
  "/auto-loans",
  "/refinance",
  "/warranty",
  "/auto-insurance/business-use-coverage",
  "/auto-insurance/employer-requirements",
  "/auto-insurance/commercial-auto-coverage",
  "/auto-insurance/cost-and-claims-decisions",
];

const HIDDEN_PREFIXES = ["/admin"];

// Iter-62: per-vertical sticky-bar copy. The default "Compare Rates · Get My
// Quote" CTA tested well as a baseline, but routing the user into the
// vertical-appropriate intake matches conversion intent. Examples:
//   /auto-insurance → "Compare Insurance Quotes"
//   /auto-loans     → "Find Loan Offers"
//   /warranty       → "Compare Protection Plans"
//   /auto-loan-refinance-calculator → "Lower My Payment"
//
// Iter-185.BI — Per-vertical CTAs unified to the user-preferred set
// (Compare Insurance Quotes / Find Loan Offers / Lower My Payment /
// Compare Protection Plans) so every surface speaks the same verb.
function resolveSticky(pathname) {
  if (pathname.startsWith("/auto-insurance") || pathname.startsWith("/insurance/")) {
    return {
      category: "insurance",
      eyebrow: "2 min · Free · No spam",
      headline: "Compare Insurance",
      cta: CTA_INSURANCE,
    };
  }
  if (
    pathname.startsWith("/auto-loan-refinance") ||
    pathname.includes("refinance") ||
    pathname.startsWith("/best-auto-loan-refinance")
  ) {
    return {
      category: "loan-refinance",
      eyebrow: "2 min · Soft pull · No spam",
      headline: "Refinance & Save",
      cta: CTA_REFINANCE,
    };
  }
  if (
    pathname.startsWith("/auto-loan") ||
    pathname.startsWith("/best-auto-loan") ||
    pathname.startsWith("/car-affordability")
  ) {
    return {
      category: "auto-loan",
      eyebrow: "2 min · Soft pull · No spam",
      headline: "Compare Loans",
      cta: CTA_AUTO_LOAN,
    };
  }
  if (pathname.startsWith("/warranty") || pathname.startsWith("/extended-warranty")) {
    return {
      category: "warranty",
      eyebrow: "Free · No obligation · 2 min",
      headline: "Compare Warranty",
      cta: CTA_WARRANTY,
    };
  }
  // Default — guides hub, compare hub, calculators hub
  return {
    category: null,
    eyebrow: "2 min · Free · Soft pull",
    headline: CTA_SAVINGS_CALCULATOR,
    cta: "Get My Quote",
  };
}

export default function MobileStickyBar() {
  const { pathname } = useLocation();
  const { open } = useQuoteModal();
  const { items: stashItems } = useCompareStash();

  if (HIDDEN_ROUTES_EXACT.includes(pathname)) return null;
  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  // Iter-192.F — Hide when the CompareDrawer is showing. The drawer
  // takes over the full-width bottom slot at z-[110]; rendering this
  // bar underneath would peek through the drawer's transparent edges
  // and visually confuse the user.
  if (stashItems.length > 0) return null;

  const { category, eyebrow, headline, cta } = resolveSticky(pathname);

  const handleClick = () => {
    track(events.CTA_CLICK, {
      cta_location: "mobile_sticky_bar",
      cta_variant: category || "default",
      page_location: pathname,
    });
    open(category);
  };

  return (
    <div
      data-testid="mobile-sticky-bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A2540] border-t border-slate-800 shadow-2xl"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <button
        type="button"
        onClick={handleClick}
        data-testid="mobile-sticky-cta"
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-white text-left"
      >
        <div className="flex flex-col leading-tight">
          <span className="font-display font-black text-lg tracking-tight">
            {headline}
          </span>
          <span className="text-[11px] text-[#10B981] font-semibold uppercase tracking-[0.15em]">
            {eyebrow}
          </span>
        </div>
        <div className="bg-[#10B981] text-[#0A2540] font-bold h-11 px-5 grid place-items-center">
          <span className="inline-flex items-center gap-1.5 text-sm">
            {cta}
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </button>
    </div>
  );
}
